import React, { FC, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@components/atoms/Card';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import styles from './ServiceCard.module.css';
import { useAddFavoriteStore } from '@services/useAddFavoriteStore';
import { useRemoveFavoriteStore } from '@services/useRemoveFavoriteStore';
import { Modules, useModule } from '@hooks/useModule';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { useData } from '@hooks/useData';

export interface ServiceCardProps {
    uuid?: string;
    link: string;
    category?: {
        name: string;
        icon: { src: string; type: string; width: number; height: number };
    };
    name: string;
    address: string;
    municipality: string;
    logo: { src: string; type: string; width: number; height: number; isDefaultLogo?: boolean };
    initialFavorite: boolean;
}

export const ServiceCard: FC<ServiceCardProps> = memo(
    ({ uuid, link, category, name, address, municipality, logo, initialFavorite }) => {
        const { isModuleActive } = useModule();
        const { allSettings } = useData();
        const {
            request: requestAddFavorite,
            loading: loadingAddFavorite,
            error: errorAddFavorite,
            lastResponseTime: lastAddFavorite,
        } = useAddFavoriteStore();
        const {
            request: requestRemoveFavorite,
            loading: loadingRemoveFavorite,
            error: errorRemoveFavorite,
            lastResponseTime: lastRemoveFavorite,
        } = useRemoveFavoriteStore();
        const { t } = useTranslation();

        const translations = {
            moreButton: t('service-card-component.more.button'),
        };

        const [favorite, setFavorite] = useState<boolean>(initialFavorite || false);

        const handleOnClickFavorite = () => {
            if (!loadingAddFavorite && !loadingRemoveFavorite) {
                favorite ? requestRemoveFavorite({ uuid: uuid || '' }) : requestAddFavorite({ uuid: uuid || '' });
            }
        };

        useEffect(() => {
            if (lastAddFavorite && !errorAddFavorite && !favorite) {
                setFavorite(true);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [lastAddFavorite]);

        useEffect(() => {
            if (lastRemoveFavorite && !errorRemoveFavorite && favorite) {
                setFavorite(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [lastRemoveFavorite]);

        return (
            <div className="position-relative">
                {/* set/unset as favorite */}
                {isModuleActive(Modules.favorite) && (
                    <div style={{ position: 'absolute', zIndex: 1, top: '10px', right: '10px' }}>
                        <RoundIcon
                            icon={`${favorite ? 'fa-solid' : 'fa-light'} fa-heart`}
                            variant={RoundIconVariants.variant3}
                            onClick={handleOnClickFavorite}
                        />
                    </div>
                )}
                <Card classes={`${styles['service-card']} title-underline-animation`}>
                    <Anchor href={link} classes="text-decoration-none" mode={AnchorMode.none}>
                        <>
                            <div className={`card-body ${styles['card-body']} ${styles['top-part']}`}>
                                {category?.icon?.src && (
                                    <img className={styles['img-icon']} src={category.icon.src} alt={category.name} />
                                )}
                                <Card.Title
                                    typography={{
                                        tagVariant: TypographyTagVariants.h5,
                                        classes: `${styles['title']} text-truncate mb-1 pe-5`,
                                        color: 'dark',
                                        fontWeight: '900',
                                        fontSize: '24',
                                        children: name,
                                        breakpointsStyles: { md: { fontSize: '20' } },
                                    }}
                                    underlineAnimation={true}
                                />
                                <Typography classes="text-truncate mb-0" color="muted">
                                    {address}
                                </Typography>
                                <Typography classes="text-truncate mb-0" color="muted">
                                    {municipality}
                                </Typography>
                            </div>
                            {logo.src && (
                                <img
                                    className={styles.logo}
                                    src={logo.src}
                                    alt={name}
                                    style={{
                                        backgroundColor: logo?.isDefaultLogo
                                            ? allSettings?.data?.layouts?.find((item) => item.name === 'primary color')?.value
                                            : 'white',
                                        ...(logo?.isDefaultLogo ? { padding: '6px' } : {}),
                                    }}
                                />
                            )}
                            <div className="card-body pb-2">
                                <Button label={translations.moreButton} mode={ButtonMode.link} />
                            </div>
                        </>
                    </Anchor>
                </Card>
            </div>
        );
    }
);
